import React, { useState, useRef } from 'react';
import { Plus, Trash2, Download, Upload } from 'lucide-react';
import axios from '../../api/axios';
import toast from 'react-hot-toast';
import { formatCurrency } from '../../utils/currency';
import { useOrganization } from '../../hooks/useOrganization';

const ManualTransactionForm = ({ accounts, onSuccess, onCancel }) => {
  const { organization } = useOrganization();
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    entries: [
      { description: '', account_id: '', debit: '', credit: '' },
      { description: '', account_id: '', debit: '', credit: '' }
    ]
  });
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showTransactions, setShowTransactions] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const fileInputRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate entries
      const validEntries = formData.entries.filter(entry => 
        entry.description && entry.account_id && (entry.debit || entry.credit)
      );

      if (validEntries.length < 2) {
        toast.error('At least 2 entries are required');
        setLoading(false);
        return;
      }

      const totalDebit = validEntries.reduce((sum, entry) => sum + parseFloat(entry.debit || 0), 0);
      const totalCredit = validEntries.reduce((sum, entry) => sum + parseFloat(entry.credit || 0), 0);

      if (Math.abs(totalDebit - totalCredit) > 0.01) {
        toast.error('Total debit must equal total credit');
        setLoading(false);
        return;
      }

      await axios.post('/api/transactions/manual', {
        date: formData.date,
        entries: validEntries.map(entry => ({
          description: entry.description,
          account_id: entry.account_id,
          debit: parseFloat(entry.debit || 0),
          credit: parseFloat(entry.credit || 0)
        }))
      });

      toast.success('Manual transaction recorded successfully');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to record transaction');
    } finally {
      setLoading(false);
    }
  };

  const handleEntryChange = (index, field, value) => {
    const newEntries = [...formData.entries];
    newEntries[index][field] = value;
    setFormData(prev => ({ ...prev, entries: newEntries }));
  };

  const addEntry = () => {
    setFormData(prev => ({
      ...prev,
      entries: [...prev.entries, { description: '', account_id: '', debit: '', credit: '' }]
    }));
  };

  const removeEntry = (index) => {
    if (formData.entries.length > 2) {
      const newEntries = formData.entries.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, entries: newEntries }));
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post('/api/transactions/manual/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const { imported, failed, errors } = response.data;
      toast.success(`Import completed: ${imported} imported, ${failed} failed`);
      
      if (errors.length > 0) {
      }
      
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Import failed');
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get('/api/transactions/manual/template', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'transaction_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const fetchRecentTransactions = async () => {
    try {
      const response = await axios.get('/api/transactions?type=MANUAL&limit=10');
      setTransactions(response.data.transactions || []);
      setShowTransactions(true);
    } catch (error) {
      toast.error('Failed to fetch transactions');
    }
  };

  const totalDebit = formData.entries.reduce((sum, entry) => sum + parseFloat(entry.debit || 0), 0);
  const totalCredit = formData.entries.reduce((sum, entry) => sum + parseFloat(entry.credit || 0), 0);

  return (
    <div className="bg-white rounded-lg shadow border p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Manual Journal Entry</h3>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={downloadTemplate}
            className="flex items-center px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
          >
            <Download className="w-3 h-3 mr-1" />
            Download Template
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className="flex items-center px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            <Upload className="w-3 h-3 mr-1" />
            {importing ? 'Importing...' : 'Import Transactions'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.csv"
            onChange={handleImport}
            className="hidden"
          />
        </div>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
          <input
            type="date"
            value={formData.date}
            onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
            required
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <h4 className="font-medium">Journal Entries</h4>
            <button
              type="button"
              onClick={addEntry}
              className="flex items-center px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
            >
              <Plus className="w-3 h-3 mr-1" />
              Add Entry
            </button>
          </div>

          {formData.entries.map((entry, index) => (
            <div key={index} className="grid grid-cols-12 gap-2 items-center p-3 border rounded">
              <div className="col-span-3">
                <input
                  type="text"
                  placeholder="Description"
                  value={entry.description}
                  onChange={(e) => handleEntryChange(index, 'description', e.target.value)}
                  className="w-full px-2 py-1 border rounded text-sm"
                />
              </div>
              
              <div className="col-span-3">
                <select
                  value={entry.account_id}
                  onChange={(e) => handleEntryChange(index, 'account_id', e.target.value)}
                  className="w-full px-2 py-1 border rounded text-sm"
                >
                  <option value="">Select Account</option>
                  {accounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="col-span-2">
                <input
                  type="number"
                  placeholder="Debit"
                  value={entry.debit}
                  onChange={(e) => handleEntryChange(index, 'debit', e.target.value)}
                  step="0.01"
                  min="0"
                  className="w-full px-2 py-1 border rounded text-sm"
                />
              </div>
              
              <div className="col-span-2">
                <input
                  type="number"
                  placeholder="Credit"
                  value={entry.credit}
                  onChange={(e) => handleEntryChange(index, 'credit', e.target.value)}
                  step="0.01"
                  min="0"
                  className="w-full px-2 py-1 border rounded text-sm"
                />
              </div>
              
              <div className="col-span-2">
                {formData.entries.length > 2 && (
                  <button
                    type="button"
                    onClick={() => removeEntry(index)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* Totals */}
          <div className="flex justify-end space-x-4 text-sm font-medium">
            <div className={`px-3 py-1 rounded ${totalDebit === totalCredit && totalDebit > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              Total Debit: {formatCurrency(totalDebit, organization?.currency_code)}
            </div>
            <div className={`px-3 py-1 rounded ${totalDebit === totalCredit && totalCredit > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              Total Credit: {formatCurrency(totalCredit, organization?.currency_code)}
            </div>
          </div>
        </div>

        <div className="flex space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || totalDebit !== totalCredit || totalDebit === 0}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Recording...' : 'Record Transaction'}
          </button>
          <button
            type="button"
            onClick={fetchRecentTransactions}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            Show Recent Transactions
          </button>
        </div>
      </form>

      {showTransactions && (
        <div className="mt-6 border-t pt-4">
          <div className="flex justify-between items-center mb-3">
            <h4 className="font-medium">Recent Manual Transactions</h4>
            <button
              onClick={() => setShowTransactions(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              ×
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Account</th>
                  <th className="px-3 py-2 text-left">Description</th>
                  <th className="px-3 py-2 text-right">Debit</th>
                  <th className="px-3 py-2 text-right">Credit</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction, index) => (
                  <tr key={index} className="border-b">
                    <td className="px-3 py-2">{new Date(transaction.date).toLocaleDateString()}</td>
                    <td className="px-3 py-2">{transaction.account?.name}</td>
                    <td className="px-3 py-2">{transaction.description}</td>
                    <td className="px-3 py-2 text-right">{transaction.debit > 0 ? formatCurrency(transaction.debit, organization?.currency_code) : '-'}</td>
                    <td className="px-3 py-2 text-right">{transaction.credit > 0 ? formatCurrency(transaction.credit, organization?.currency_code) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualTransactionForm;
